// src/index.ts
import { computed, reactive, ref } from "vue";

// src/interpolate.ts
var EVALUATION_RE = /[[\].]{1,2}/g;
var INTERPOLATION_RE = /%\{((?:.|\n)+?)\}/g;
var MUSTACHE_SYNTAX_RE = /\{\{((?:.|\n)+?)\}\}/g;
var interpolate = (plugin) => (msgid, context = {}, parent) => {
  const silent = plugin.silent;
  if (!silent && MUSTACHE_SYNTAX_RE.test(msgid)) {
    console.warn(`Mustache syntax cannot be used with vue-gettext. Please use "%{}" instead of "{{}}" in: ${msgid}`);
  }
  const result = msgid.replace(INTERPOLATION_RE, (_match, token) => {
    const expression = token.trim();
    let evaluated;
    const escapeHtmlMap = {
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      '"': "&quot;",
      "'": "&#039;"
    };
    function getProps(obj, expression2) {
      const arr = expression2.split(EVALUATION_RE).filter((x) => x);
      while (arr.length) {
        obj = obj[arr.shift()];
      }
      return obj;
    }
    function evalInContext(context2, expression2, parent2) {
      try {
        evaluated = getProps(context2, expression2);
      } catch (e) {
      }
      if (evaluated === void 0 || evaluated === null) {
        if (parent2) {
          return evalInContext(parent2.ctx, expression2, parent2.parent);
        } else {
          console.warn(`Cannot evaluate expression: ${expression2}`);
          evaluated = expression2;
        }
      }
      const result2 = evaluated.toString();
      return result2;
    }
    return evalInContext(context, expression, parent);
  });
  return result;
};
interpolate.INTERPOLATION_RE = INTERPOLATION_RE;
interpolate.INTERPOLATION_PREFIX = "%{";
var interpolate_default = interpolate;

// src/plurals.ts
var plurals_default = {
  getTranslationIndex: function(languageCode, n) {
    n = Number(n);
    n = typeof n === "number" && isNaN(n) ? 1 : n;
    if (languageCode.length > 2 && languageCode !== "pt_BR") {
      languageCode = languageCode.split("_")[0];
    }
    switch (languageCode) {
      case "ay":
      case "bo":
      case "cgg":
      case "dz":
      case "fa":
      case "id":
      case "ja":
      case "jbo":
      case "ka":
      case "kk":
      case "km":
      case "ko":
      case "ky":
      case "lo":
      case "ms":
      case "my":
      case "sah":
      case "su":
      case "th":
      case "tt":
      case "ug":
      case "vi":
      case "wo":
      case "zh":
        return 0;
      case "is":
        return n % 10 !== 1 || n % 100 === 11 ? 1 : 0;
      case "jv":
        return n !== 0 ? 1 : 0;
      case "mk":
        return n === 1 || n % 10 === 1 ? 0 : 1;
      case "ach":
      case "ak":
      case "am":
      case "arn":
      case "br":
      case "fil":
      case "fr":
      case "gun":
      case "ln":
      case "mfe":
      case "mg":
      case "mi":
      case "oc":
      case "pt_BR":
      case "tg":
      case "ti":
      case "tr":
      case "uz":
      case "wa":
        return n > 1 ? 1 : 0;
      case "lv":
        return n % 10 === 1 && n % 100 !== 11 ? 0 : n !== 0 ? 1 : 2;
      case "lt":
        return n % 10 === 1 && n % 100 !== 11 ? 0 : n % 10 >= 2 && (n % 100 < 10 || n % 100 >= 20) ? 1 : 2;
      case "be":
      case "bs":
      case "hr":
      case "ru":
      case "sr":
      case "uk":
        return n % 10 === 1 && n % 100 !== 11 ? 0 : n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 10 || n % 100 >= 20) ? 1 : 2;
      case "mnk":
        return n === 0 ? 0 : n === 1 ? 1 : 2;
      case "ro":
        return n === 1 ? 0 : n === 0 || n % 100 > 0 && n % 100 < 20 ? 1 : 2;
      case "pl":
        return n === 1 ? 0 : n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 10 || n % 100 >= 20) ? 1 : 2;
      case "cs":
      case "sk":
        return n === 1 ? 0 : n >= 2 && n <= 4 ? 1 : 2;
      case "csb":
        return n === 1 ? 0 : n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 10 || n % 100 >= 20) ? 1 : 2;
      case "sl":
        return n % 100 === 1 ? 0 : n % 100 === 2 ? 1 : n % 100 === 3 || n % 100 === 4 ? 2 : 3;
      case "mt":
        return n === 1 ? 0 : n === 0 || n % 100 > 1 && n % 100 < 11 ? 1 : n % 100 > 10 && n % 100 < 20 ? 2 : 3;
      case "gd":
        return n === 1 || n === 11 ? 0 : n === 2 || n === 12 ? 1 : n > 2 && n < 20 ? 2 : 3;
      case "cy":
        return n === 1 ? 0 : n === 2 ? 1 : n !== 8 && n !== 11 ? 2 : 3;
      case "kw":
        return n === 1 ? 0 : n === 2 ? 1 : n === 3 ? 2 : 3;
      case "ga":
        return n === 1 ? 0 : n === 2 ? 1 : n > 2 && n < 7 ? 2 : n > 6 && n < 11 ? 3 : 4;
      case "ar":
        return n === 0 ? 0 : n === 1 ? 1 : n === 2 ? 2 : n % 100 >= 3 && n % 100 <= 10 ? 3 : n % 100 >= 11 ? 4 : 5;
      default:
        return n !== 1 ? 1 : 0;
    }
  }
};

// src/utilities.ts
import { inject } from "vue";

// src/typeDefs.ts
var GetTextSymbol = Symbol("GETTEXT");

// src/utilities.ts
function normalizeMsgId(key) {
  return key.replace(/\r?\n|\r/, "").trim();
}
function normalizeTranslations(translations) {
  const newTranslations = {};
  Object.keys(translations).forEach((lang) => {
    const langData = translations[lang];
    const newLangData = {};
    Object.keys(langData).forEach((key) => {
      newLangData[normalizeMsgId(key)] = langData[key];
    });
    newTranslations[lang] = newLangData;
  });
  return newTranslations;
}
var useGettext = () => {
  const gettext = inject(GetTextSymbol, null);
  if (!gettext) {
    throw new Error("Failed to inject gettext. Make sure vue3-gettext is set up properly.");
  }
  return gettext;
};
function assertIsDefined(value) {
  if (value === void 0 || value === null) {
    throw new Error(`${value} is not defined`);
  }
}

// src/translate.ts
var translate = (language) => ({
  /*
   * Get the translated string from the translation.json file.
   *
   * @param {String} msgid - The translation key
   * @param {Number} n - The number to switch between singular and plural
   * @param {String} context - The translation key context
   * @param {String} defaultPlural - The default plural value (optional)
   * @param {String} languageKey - The language ID (e.g. 'fr_FR' or 'en_US')
   * @param {Object} args - Values to be used when strings are interpolated
   *
   * @return {String} The translated string
   */
  getTranslation: function(msgid, n = 1, context = null, defaultPlural = null, languageKey, args) {
    if (languageKey === void 0) {
      languageKey = language.current;
    }
    const interp = (message, parameters) => parameters ? language.interpolate(message, parameters) : message;
    msgid = normalizeMsgId(msgid);
    defaultPlural = defaultPlural ? normalizeMsgId(defaultPlural) : null;
    if (!msgid) {
      return "";
    }
    const silent = languageKey ? language.silent || language.muted.indexOf(languageKey) !== -1 : false;
    let noTransLangKey = "en";
    if (language.sourceCodeLanguage) {
      noTransLangKey = language.sourceCodeLanguage;
    }
    const untranslated = defaultPlural && plurals_default.getTranslationIndex(noTransLangKey, n) > 0 ? defaultPlural : msgid;
    const pluginTranslations = language.translations;
    const translations = pluginTranslations[languageKey] || pluginTranslations[languageKey.split("_")[0]];
    if (!translations) {
      if (!silent) {
        console.warn(`No translations found for ${languageKey}`);
      }
      return interp(untranslated, args);
    }
    const getTranslationFromArray = (arr) => {
      let translationIndex = plurals_default.getTranslationIndex(languageKey, n);
      if (arr.length === 1 && n === 1) {
        translationIndex = 0;
      }
      const str = arr[translationIndex];
      if (!str) {
        if (str === "") {
          return interp(untranslated, args);
        }
        throw new Error(msgid + " " + translationIndex + " " + language.current + " " + n);
      }
      return interp(str, args);
    };
    const getUntranslatedMsg = () => {
      if (!silent) {
        let msg = `Untranslated ${languageKey} key found: ${msgid}`;
        if (context) {
          msg += ` (with context: ${context})`;
        }
        console.warn(msg);
      }
      return interp(untranslated, args);
    };
    const translateMsg = (msg, context2 = null) => {
      if (msg instanceof Object) {
        if (Array.isArray(msg)) {
          return getTranslationFromArray(msg);
        }
        const msgContext = context2 != null ? context2 : "";
        const ctxVal = msg[msgContext];
        return translateMsg(ctxVal);
      }
      if (context2) {
        return getUntranslatedMsg();
      }
      if (!msg) {
        return getUntranslatedMsg();
      }
      return interp(msg, args);
    };
    const translated = translations[msgid];
    return translateMsg(translated, context);
  },
  /*
   * Returns a string of the translation of the message.
   * Also makes the string discoverable by gettext-extract.
   *
   * @param {String} msgid - The translation key
   * @param {Object} parameters - The interpolation parameters
   *
   * @return {String} The translated string
   */
  gettext: function(msgid, parameters) {
    return this.getTranslation(msgid, void 0, void 0, void 0, void 0, parameters);
  },
  /*
   * Returns a string of the translation for the given context.
   * Also makes the string discoverable by gettext-extract.
   *
   * @param {String} context - The context of the string to translate
   * @param {String} msgid - The translation key
   * @param {Object} parameters - The interpolation parameters
   *
   * @return {String} The translated string
   */
  pgettext: function(context, msgid, parameters) {
    return this.getTranslation(msgid, 1, context, void 0, void 0, parameters);
  },
  /*
   * Returns a string of the translation of either the singular or plural,
   * based on the number.
   * Also makes the string discoverable by gettext-extract.
   *
   * @param {String} msgid - The translation key
   * @param {String} plural - The plural form of the translation key
   * @param {Number} n - The number to switch between singular and plural
   * @param {Object} parameters - The interpolation parameters
   *
   * @return {String} The translated string
   */
  ngettext: function(msgid, plural, n, parameters) {
    return this.getTranslation(msgid, n, null, plural, void 0, parameters);
  },
  /*
   * Returns a string of the translation of either the singular or plural,
   * based on the number, for the given context.
   * Also makes the string discoverable by gettext-extract.
   *
   * @param {String} context - The context of the string to translate
   * @param {String} msgid - The translation key
   * @param {String} plural - The plural form of the translation key
   * @param {Number} n - The number to switch between singular and plural
   * @param {Object} parameters - The interpolation parameters
   *
   * @return {String} The translated string
   */
  npgettext: function(context, msgid, plural, n, parameters) {
    return this.getTranslation(msgid, n, context, plural, void 0, parameters);
  }
});
var translate_default = translate;

// src/extract/tokenizer.ts
function tokenize(mapping, src) {
  const tokens = [];
  let idx = -1;
  let unrecognizedContent = "";
  const keywords = Object.values(mapping).flat();
  const maxKeywordLen = keywords.reduce((prev, curr) => {
    if (curr.length > prev) {
      return curr.length;
    }
    return prev;
  }, 0);
  function advance() {
    idx += 1;
    return src.charAt(idx);
  }
  function addToken(kind, charIndex, value) {
    if (unrecognizedContent.trim()) {
      tokens.push({ kind: "Unrecognized" /* Unrecognized */, idx, value: unrecognizedContent });
      unrecognizedContent = "";
    }
    if (value) {
      tokens.push({ kind, idx: charIndex, value });
      return;
    }
    tokens.push({ kind, idx: charIndex });
  }
  function readString(delimiter) {
    let content = "";
    let prevChar = delimiter;
    let c = advance();
    function advanceChar() {
      prevChar = c;
      c = advance();
    }
    while (true) {
      if (c === "") {
        console.error(`parsing error, string literal is not closed until end of file`);
        break;
      }
      if (prevChar !== "\\") {
        if (c === "\\") {
          advanceChar();
          continue;
        }
        if (c === delimiter) {
          break;
        }
      }
      const backSlashInserted = c === "\\";
      content += c;
      advanceChar();
      if (backSlashInserted) {
        prevChar = "\\\\";
      }
    }
    return content.replace(/\r\n/g, "\n");
  }
  function scanToken() {
    var _a;
    const c = advance();
    switch (c) {
      case "(":
        addToken("ParenLeft" /* ParenLeft */, idx);
        break;
      case ",":
        addToken("Comma" /* Comma */, idx);
        break;
      case '"':
      case "'":
      case "`":
        const prevTokenKind = (_a = tokens[tokens.length - 1]) == null ? void 0 : _a.kind;
        if (!unrecognizedContent.trim() && (prevTokenKind === "ParenLeft" /* ParenLeft */ || prevTokenKind === "Comma" /* Comma */)) {
          addToken("String" /* String */, idx, readString(c));
          break;
        }
      default:
        if (c.match(/\s\n\r/)) {
          break;
        }
        const possibleKeyword = src.substring(idx, idx + maxKeywordLen);
        const matchedKeyword = keywords.filter((kw) => possibleKeyword.startsWith(kw)).reduce(
          (prev, curr) => {
            var _a2;
            if (curr.length > ((_a2 = prev == null ? void 0 : prev.length) != null ? _a2 : 0)) {
              return curr;
            }
            return prev;
          },
          void 0
        );
        if (matchedKeyword) {
          addToken("Keyword" /* Keyword */, idx, matchedKeyword);
          idx += matchedKeyword.length - 1;
          break;
        }
        unrecognizedContent += c;
        break;
    }
  }
  while (idx < src.length) {
    scanToken();
  }
  return tokens;
}

// src/extract/parser.ts
import PO from "pofile";
function parseFunctionCall(mapping, tokens) {
  var _a;
  let idx = -1;
  let t = void 0;
  function advance() {
    idx += 1;
    return tokens[idx];
  }
  const msgs = [];
  while (true) {
    t = advance();
    if (!t) {
      break;
    }
    if (t.kind !== "Keyword" /* Keyword */) {
      continue;
    }
    assertIsDefined(t.value);
    const keyword = t.value;
    const charIndex = t.idx;
    if (((_a = tokens[idx + 1]) == null ? void 0 : _a.kind) !== "ParenLeft" /* ParenLeft */) {
      continue;
    }
    t = advance();
    t = advance();
    const stringArgs = [];
    while (true) {
      if (t.kind !== "String" /* String */) {
        break;
      }
      assertIsDefined(t.value);
      stringArgs.push(t.value);
      t = advance();
      if (!t) {
        break;
      }
      if (t.kind !== "Comma" /* Comma */) {
        break;
      }
      t = advance();
    }
    msgs.push(getMsgInfo(mapping, keyword, charIndex, stringArgs));
  }
  return msgs;
}
function getMsgInfo(mapping, keyword, charIdx, stringArgs) {
  var _a, _b, _c, _d;
  if ((_a = mapping.simple) == null ? void 0 : _a.includes(keyword)) {
    return {
      message: stringArgs[0],
      idx: charIdx
    };
  }
  if ((_b = mapping.plural) == null ? void 0 : _b.includes(keyword)) {
    return {
      message: stringArgs[0],
      messagePlural: stringArgs[1],
      idx: charIdx
    };
  }
  if ((_c = mapping.ctx) == null ? void 0 : _c.includes(keyword)) {
    return {
      context: stringArgs[0],
      message: stringArgs[1],
      idx: charIdx
    };
  }
  if ((_d = mapping.ctxPlural) == null ? void 0 : _d.includes(keyword)) {
    return {
      context: stringArgs[0],
      message: stringArgs[1],
      messagePlural: stringArgs[2],
      idx: charIdx
    };
  }
  throw new Error(`no mapping found for keyword "${keyword}", please report this error`);
}
function getKeywords(keywords, overrideDefaults = false) {
  if (overrideDefaults) {
    if (!keywords) {
      throw new Error(`overrideDefaults is enabled but no keywords provided in config`);
    }
    return keywords;
  }
  return {
    simple: ["$gettext", ...(keywords == null ? void 0 : keywords.simple) ? keywords == null ? void 0 : keywords.simple : []],
    plural: ["$ngettext", ...(keywords == null ? void 0 : keywords.plural) ? keywords == null ? void 0 : keywords.plural : []],
    ctxPlural: ["$npgettext", ...(keywords == null ? void 0 : keywords.ctxPlural) ? keywords == null ? void 0 : keywords.ctxPlural : []],
    ctx: ["$pgettext", ...(keywords == null ? void 0 : keywords.ctx) ? keywords == null ? void 0 : keywords.ctx : []]
  };
}
function parseSrc(src, options) {
  const keywords = getKeywords(options == null ? void 0 : options.mapping, options == null ? void 0 : options.overrideDefaults);
  const tokens = tokenize(keywords, src);
  const msgs = parseFunctionCall(keywords, tokens);
  return msgs.map((info) => {
    const i = { ...info };
    delete i.idx;
    return {
      ...i,
      lineNumber: src.substring(0, info.idx).split("\n").length
    };
  });
}
function makePO(fileName, msgs) {
  const po = new PO();
  for (const msg of msgs) {
    const item = new PO.Item();
    item.msgid = msg.message;
    item.msgid_plural = msg.messagePlural;
    item.msgctxt = msg.context;
    item.references = [`${fileName}:${msg.lineNumber}`];
    po.items.push(item);
  }
  return po;
}

// src/index.ts
var defaultOptions = {
  /** all the available languages of your application. Keys must match locale names */
  availableLanguages: { en: "English" },
  defaultLanguage: "en",
  sourceCodeLanguage: void 0,
  mutedLanguages: [],
  silent: false,
  translations: {},
  setGlobalProperties: true,
  globalProperties: {
    language: ["$language"],
    gettext: ["$gettext"],
    pgettext: ["$pgettext"],
    ngettext: ["$ngettext"],
    npgettext: ["$npgettext"],
    interpolate: ["$gettextInterpolate"]
  }
};
function createGettext(options = {}) {
  Object.keys(options).forEach((key) => {
    if (Object.keys(defaultOptions).indexOf(key) === -1) {
      throw new Error(`${key} is an invalid option for the translate plugin.`);
    }
  });
  const mergedOptions = {
    ...defaultOptions,
    ...options
  };
  const translations = ref(normalizeTranslations(mergedOptions.translations));
  const gettext = reactive({
    available: mergedOptions.availableLanguages,
    muted: mergedOptions.mutedLanguages,
    silent: mergedOptions.silent,
    translations: computed({
      get: () => {
        return translations.value;
      },
      set: (val) => {
        translations.value = normalizeTranslations(val);
      }
    }),
    current: mergedOptions.defaultLanguage,
    sourceCodeLanguage: mergedOptions.sourceCodeLanguage,
    install(app) {
      app[GetTextSymbol] = gettext;
      app.provide(GetTextSymbol, gettext);
      if (mergedOptions.setGlobalProperties) {
        const globalProperties = app.config.globalProperties;
        let properties = mergedOptions.globalProperties.gettext || ["$gettext"];
        properties.forEach((p) => {
          globalProperties[p] = gettext.$gettext;
        });
        properties = mergedOptions.globalProperties.pgettext || ["$pgettext"];
        properties.forEach((p) => {
          globalProperties[p] = gettext.$pgettext;
        });
        properties = mergedOptions.globalProperties.ngettext || ["$ngettext"];
        properties.forEach((p) => {
          globalProperties[p] = gettext.$ngettext;
        });
        properties = mergedOptions.globalProperties.npgettext || ["$npgettext"];
        properties.forEach((p) => {
          globalProperties[p] = gettext.$npgettext;
        });
        properties = mergedOptions.globalProperties.language || ["$language"];
        properties.forEach((p) => {
          globalProperties[p] = gettext;
        });
      }
    }
  });
  const translate2 = translate_default(gettext);
  const interpolate2 = interpolate_default(gettext);
  gettext.$gettext = translate2.gettext.bind(translate2);
  gettext.$pgettext = translate2.pgettext.bind(translate2);
  gettext.$ngettext = translate2.ngettext.bind(translate2);
  gettext.$npgettext = translate2.npgettext.bind(translate2);
  gettext.interpolate = interpolate2.bind(interpolate2);
  return gettext;
}
var defineGettextConfig = (config) => {
  return config;
};
export {
  createGettext,
  defineGettextConfig,
  makePO,
  parseSrc,
  tokenize,
  useGettext
};
